import { useState } from "react";
import Divider from '@mui/material/Divider';
import Backdrop from '@mui/material/Backdrop';
import TextField from '@mui/material/TextField';
import LinearProgressWithLabel from '../components/linear-progress-with-label';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid2';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoadingMetadataTable from '../components/loading-metadata-table'
import { useAuth } from 'oidc-react';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useNotifications } from '@toolpad/core/useNotifications';
import Constants from '../config/constants';
import { dataToChunks } from '../utils/util';
import { handleCsvDownload, resolveCsvName } from '../utils/file-util';
import { FileService } from '../services/file-service';


const DNS_ORIGINAL = 'https://prestige.circle-arts.com'
const DNS_FRIENDLY = 'https://vali.circle-arts.com'

const NAME_TEMPLATE = 'CFA_MyReview_2025_{name}.jpg';


function UtilityProofPageContent({ pathname }) {
	const auth = useAuth();
	const dialogs = useDialogs();
	const notifications = useNotifications();

	const [data, setData] = useState(null);
	const [progress, setProgress] = useState(0);
	const [showLoader, setShowLoader] = useState(false);
	const [nameTemplate, setNameTemplate] = useState(NAME_TEMPLATE);

	const fileService = new FileService(auth);


	const onDataUpload = (data) => {
		setData(data);
	};

	const handleUpload = async () => {
		const projectShortCode = await dialogs.prompt('Please specify the project short-code', {
			okText: 'Upload',
			cancelText: 'Cancel',
		});

		if (!projectShortCode) {
			return;
		}

		try {
			setShowLoader(true);
			let operationsCompleted = 0;

			const payloads = (await buildPayloadChunks(data, projectShortCode))
				.map(p => fileService.uploadMany(p)
					.finally(() => increaseAndSet()));

			increaseAndSet();

			const results = (await Promise
				.all(payloads))
				.flatMap(r => r)
				.map(r => ({ ...r, link: "\"" + r.link.replace(DNS_ORIGINAL, DNS_FRIENDLY) + "\"" }));

			const merged = results
				.reduce((a, b) => {
					let obj = a[b.artistName];
					if (obj !== undefined) {
						obj.link = obj.link.substring(0, obj.link.length - 1) + ', ' + b.link.substring(1, obj.link.length);
					} else {
						a[b.artistName] = b;
					}

					return a;
				}, {});

			handleCsvDownload(Object.values(merged), resolveCsvName(nameTemplate));

			setShowLoader(false);

			notifications.show('Proofs uploaded successfully.', Constants.NOTIFICATION_SUCCESS);

			function increaseAndSet() {
				setProgress(Math.min(100, Math.round(100 * (operationsCompleted++ / payloads.length))));
			}
		} catch (e) {
			notifications.show('Could not upload proofs. ' + e, Constants.NOTIFICATION_ERROR);
		} finally {
			setShowLoader(false);
		}
	};

	const buildPayloadChunks = async (data, projectShortCode) => {
		const digests = await Promise.all(data
			.map(async i => ({
				id: i.id,
				artist: i.artist,
				page: i.page,
				content: i.content,
				contentType: i.contentType,
			})));

		return dataToChunks(digests, 1)
			.map(c => {
				return c
					.filter(n => n)
					.map(n => ({
						artistName: n.artist,
						filename: projectShortCode + "/" + nameTemplate
							.replace("{page}", n.page)
							.replace("{name}", n.artist),
						contentAsBase64: n.content,
						contentType: n.contentType,
					}))
			});
	};

	function columns() {
		return [
			{
				width: 50,
				label: 'Image',
				dataKey: 'image',
				type: "IMAGE",
			},
			{
				width: 100,
				label: 'Artist Name',
				dataKey: 'artist',
				type: "CALCULATED",
				fn: (rows) => {
					rows
						.forEach(r => {
							console.log(nameTemplate);
							let matches = r.path.matchAll(nameTemplate
								.replace("{page}", ".*")
								.replace("{name}", "(.*)"));

							for (const match of matches) {
								r.artist = match[1];
							}

							matches = r.path.matchAll(nameTemplate
								.replace("{name}", ".*")
								.replace("{page}", "(.*)"));

							for (const match of matches) {
								r.page = match[1];
							}
						});
				}
			},
		];
	}

	return (
		<>
			<Divider sx={{ m: 2 }} />
			<Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={showLoader} >
				<LinearProgressWithLabel value={progress} />
			</Backdrop>
			<Grid container spacing={4} padding={2} columns={{ xs: 2, sm: 6, md: 6, xl: 12 }}>
				<Grid size={12}>
					<LoadingMetadataTable onUpload={onDataUpload} columns={columns} />
				</Grid>

				<Grid size={12}>
					<Accordion defaultExpanded>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="proof-filename-content"
							id="proof-filename-header"
						>
							Proof filename template
						</AccordionSummary>
						<AccordionDetails>
							<TextField value={nameTemplate} label={'Name Template'} onChange={(v) => setNameTemplate(v.target.value)} required fullWidth />
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid size={12}>
					<LoadingButton variant="contained" disabled={!data} onClick={handleUpload}>
						Upload
					</LoadingButton>
				</Grid>
			</Grid>
		</>
	);
}

export default UtilityProofPageContent;